import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MagnifyingGlass, Article, BookOpen, Quotes, Heart, Newspaper, 
  X, ArrowRight, Sparkle, Scroll, GraduationCap, Books,
  MaskHappy, Baby, UserCircle, FileMagnifyingGlass, Microphone, Megaphone
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';

const CONTENT_TYPE_ICONS = {
  poem: Article,
  story: BookOpen,
  quote: Quotes,
  shayari: Heart,
  article: Newspaper,
  ghazal: Sparkle,
  doha: Scroll,
  essay: GraduationCap,
  book: Books,
  play: MaskHappy,
  children_literature: Baby,
  memoir: UserCircle,
  literary_review: MagnifyingGlass,
  research_paper: FileMagnifyingGlass,
  interview: Microphone,
  speech: Megaphone,
};

const CATEGORY_COLORS = {
  poetry: 'from-purple-500 to-pink-500',
  prose: 'from-blue-500 to-cyan-500',
  editorial: 'from-orange-500 to-red-500',
  special: 'from-green-500 to-teal-500',
};

export default function ContentCreationHub() {
  const navigate = useNavigate();
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchContentTypes();
  }, []);

  const fetchContentTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/content/types`,
        { withCredentials: true }
      );
      setContentTypes(response.data);
    } catch (error) {
      console.error('Error fetching content types:', error);
      toast.error('Failed to load content types');
    } finally {
      setLoading(false);
    }
  };

  // Group content types by category
  const groupedTypes = contentTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {});

  // Filter based on search and category
  const filteredTypes = contentTypes.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         type.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || type.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSelectType = (typeId) => {
    navigate(`/editor/new/${typeId}`);
  };

  const categories = [
    { id: 'all', label: 'All Content', color: 'gray' },
    { id: 'poetry', label: 'Poetry', color: 'purple' },
    { id: 'prose', label: 'Prose', color: 'blue' },
    { id: 'editorial', label: 'Editorial', color: 'orange' },
    { id: 'special', label: 'Special', color: 'green' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <X size={16} />
            Close
          </button>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Create New Content
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the type of literary content you want to publish
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search content types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-muted/50 border border-border rounded-full outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Content Type Cards - Grouped by Category */}
        {selectedCategory === 'all' ? (
          // Show grouped by category
          <div className="space-y-12">
            {Object.entries(groupedTypes).map(([category, types]) => (
              <div key={category}>
                <h2 className="text-2xl font-semibold mb-6 capitalize" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {types.map(type => (
                    <ContentTypeCard
                      key={type.type_id}
                      type={type}
                      onSelect={handleSelectType}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Show filtered results
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTypes.map(type => (
              <ContentTypeCard
                key={type.type_id}
                type={type}
                onSelect={handleSelectType}
              />
            ))}
          </div>
        )}

        {/* No Results */}
        {filteredTypes.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">No content types found</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ContentTypeCard({ type, onSelect }) {
  const Icon = CONTENT_TYPE_ICONS[type.type_id] || Article;
  const gradientColor = CATEGORY_COLORS[type.category] || 'from-gray-500 to-gray-600';

  return (
    <button
      onClick={() => type.available && onSelect(type.type_id)}
      disabled={!type.available}
      className={`
        group relative p-6 rounded-2xl border-2 transition-all duration-300
        ${type.available 
          ? 'border-border hover:border-primary hover:shadow-xl hover:scale-105 cursor-pointer' 
          : 'border-border opacity-50 cursor-not-allowed'
        }
        bg-card
      `}
    >
      {/* Gradient Background on Hover */}
      {type.available && (
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradientColor} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
      )}

      {/* Content */}
      <div className="relative">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${gradientColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
          <Icon size={32} weight="duotone" className="text-white" />
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {type.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4">
          {type.description}
        </p>

        {/* Badge */}
        {type.requires_approval && (
          <span className="inline-block px-2 py-1 text-xs rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
            Requires Approval
          </span>
        )}

        {!type.available && (
          <span className="inline-block px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border">
            No Permission
          </span>
        )}

        {/* Arrow Icon on Hover */}
        {type.available && (
          <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ArrowRight size={24} className="text-primary" />
          </div>
        )}
      </div>
    </button>
  );
}
